import {
  Card,
  CrudMode,
  DataDisplay,
  formatPhoneNumber,
  Grid,
  GridBreak,
  pn,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import { AddressFields, Input, Select } from "components";
import { Address, Partner, PartnerViewResource } from "generated/openApi";

import produce from "immer";
import ApiService from "services/apiService";

export const PartnerBasicInfo = (props: {
  partner: Partner;
  setPartner: (value: Partner) => void;
  crudMode: CrudMode;
  viewResource: PartnerViewResource;
  validationErrors: ValidationErrors;
}) => {
  const { breakpointsRef, smallFieldBreakpoints, fullFieldBreakpoints } = useElementBreakpoints();
  const properties = props.viewResource.properties || [];

  const addressProperties = properties.find((x) => x.propertyName === pn<Partner>("address"))?.children || [];
  const addressValidationErrors = props.validationErrors[pn<Partner>("address")] || [];

  const nameField = () => {
    const propertyName = pn<Partner>("name");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.partner.name}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.partner.name}
        onChange={(e) =>
          props.setPartner(
            produce(props.partner, (draft) => {
              draft.name = e.target.value;
            })
          )
        }
      />
    );
  };

  const contactContributorField = () => {
    const propertyName = pn<Partner>("contactContributorId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        {props.partner.contactContributor && (
          <>
            {props.partner.contactContributor.lastName}, {props.partner.contactContributor.firstName}
          </>
        )}
      </DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.partner.contactContributor}
        getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
        onChange={(e, value) =>
          props.setPartner(
            produce(props.partner, (draft) => {
              draft.contactContributorId = value?.id;
              draft.contactContributor = value;
            })
          )
        }
        fetchFunction={(searchString: string) => ApiService.search.contributors(searchString)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const phoneField = () => {
    const propertyName = pn<Partner>("phone");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatPhoneNumber(props.partner.phone)}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.partner.phone}
        onChange={(e) =>
          props.setPartner(
            produce(props.partner, (draft) => {
              draft.phone = e.target.value;
            })
          )
        }
      />
    );
  };

  const faxField = () => {
    const propertyName = pn<Partner>("fax");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatPhoneNumber(props.partner.fax)}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.partner.fax}
        onChange={(e) =>
          props.setPartner(
            produce(props.partner, (draft) => {
              draft.fax = e.target.value;
            })
          )
        }
      />
    );
  };

  return (
    <Card header={"Info"}>
      <Grid ref={breakpointsRef} container spacing="sm">
        <Grid item xs={smallFieldBreakpoints}>
          {nameField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {contactContributorField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {phoneField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {faxField()}
        </Grid>

        <GridBreak />

        <Grid item xs={fullFieldBreakpoints}>
          <AddressFields
            crudMode={props.crudMode}
            properties={addressProperties}
            address={props.partner.address || ({} as Address)}
            setAddress={(address) =>
              props.setPartner(
                produce(props.partner, (draft) => {
                  draft.address = address;
                })
              )
            }
            validationErrors={addressValidationErrors}
          />
        </Grid>
      </Grid>
    </Card>
  );
};
